if (!customElements.get('live-with-povison')) {
  customElements.define(
    'live-with-povison',
    class LiveWithPovison extends HTMLElement {
      connectedCallback() {
        this.enableSlider = this.dataset.enableSlider === 'true';
        if (!this.enableSlider) return;

        this.slider = this.querySelector('.swiper');
        if (!this.slider) return;

        this.handleChange = this.init.bind(this);
        this.mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);

        if (this.mql.addEventListener) {
          this.mql.addEventListener('change', this.handleChange);
        } else {
          this.mql.onchange = this.handleChange;
        }

        this.init();
      }

      disconnectedCallback() {
        if (this.mql) {
          if (this.mql.removeEventListener) {
            this.mql.removeEventListener('change', this.handleChange);
          } else {
            this.mql.onchange = null;
          }
        }

        this.destroySlider();
      }

      init() {
        this.destroySlider();
        this.initSlider();
      }

      initSlider() {
        const section = this.closest('.section--live-with-povison');
        const gapDesktop = Number(this.dataset.gap) || 18;
        const gapMobile = Number(this.dataset.gapMobile) || gapDesktop;
        const gap = (this.mql && this.mql.matches) ? gapMobile : gapDesktop;
        const slidesDesktop = Number(this.dataset.slidesDesktop) || 5;
        const slidesTablet = Number(this.dataset.slidesTablet) || 3;
        const slidesMobile = Number(this.dataset.slidesMobile) || 2;

        this.sliderInstance = new window.FoxTheme.Carousel(this.slider, {
          slidesPerView: slidesMobile,
          spaceBetween: gap,
          breakpoints: {
            768: {
              slidesPerView: slidesTablet,
              spaceBetween: gap,
            },
            1024: {
              slidesPerView: slidesDesktop,
              spaceBetween: gap,
            },
          },
          navigation: {
            nextEl: section.querySelector('.swiper-button-next'),
            prevEl: section.querySelector('.swiper-button-prev'),
          },
          threshold: 2,
          watchOverflow: true,
          grabCursor: true,
        });

        this.sliderInstance.init();
      }

      destroySlider() {
        if (typeof this.sliderInstance !== 'object') return;
        this.sliderInstance.slider.destroy();
        this.sliderInstance = false;
      }
    }
  );
}

// ─── Live With Povison Modal ────────────────────────────────────────────────
class LWPModal {
  constructor(section) {
    this.section = section;
    this.modal = section.querySelector('.lwp-modal');
    if (!this.modal) return;

    this.contentEl = this.modal.querySelector('.lwp-modal__content');
    this.overlay = this.modal.querySelector('.lwp-modal__overlay');
    this._handleKeyDown = this._onKeyDown.bind(this);
    this._handleProductAdded = this._onProductAdded.bind(this);
    this._submittedFromModal = false;

    // collect block ids for modal navigation
    this._blockIds = Array.from(this.section.querySelectorAll('template[data-modal-template]')).map((t) => t.getAttribute('data-modal-template'));
    this._currentBlockId = null;

    // modal-level prev/next buttons (persistent)
    this._navPrev = this.modal.querySelector('.lwp-modal__block-prev');
    this._navNext = this.modal.querySelector('.lwp-modal__block-next');
    if (this._navPrev) this._navPrev.addEventListener('click', () => this._goToRelative(-1));
    if (this._navNext) this._navNext.addEventListener('click', () => this._goToRelative(1));

    section.querySelectorAll('[data-modal-id]').forEach((btn) => {
      btn.addEventListener('click', () => this.open(btn.dataset.modalId));
    });

    this.overlay.addEventListener('click', () => this.close());
    document.addEventListener('product-ajax:added', this._handleProductAdded);
  }

  open(blockId) {
    const tpl = this.section.querySelector(`template[data-modal-template="${blockId}"]`);
    if (!tpl) return;

    this.contentEl.innerHTML = '';
    this.contentEl.appendChild(tpl.content.cloneNode(true));

    // track current block and update nav visibility
    this._currentBlockId = blockId;
    this._updateNavState();

    this.modal.hidden = false;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', this._handleKeyDown);

    // bind all close buttons inside cloned content
    const closeBtns = this.contentEl.querySelectorAll('.lwp-modal__close-btn');
    if (closeBtns && closeBtns.length) {
      closeBtns.forEach((btn) => btn.addEventListener('click', () => this.close()));
      // focus the first visible close button if present
      const first = Array.from(closeBtns).find((b) => {
        const style = window.getComputedStyle(b);
        return style && style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
      }) || closeBtns[0];
      if (first) first.focus();
    }

    // init media switcher (images + videos)
    this._initMediaSlider();

    const atcForm = this.contentEl.querySelector('.lwp-modal__atc-form');
    if (atcForm) {
      atcForm.addEventListener('submit', () => {
        this._submittedFromModal = true;
      });
    }

    // meta Show More toggle (mobile)
    const meta = this.contentEl.querySelector('.lwp-modal__meta');
    const metaToggle = this.contentEl.querySelector('.lwp-modal__meta-toggle');
    if (metaToggle && meta) {
      metaToggle.setAttribute('aria-expanded', 'false');
      metaToggle.textContent = 'Show More';
      metaToggle.addEventListener('click', () => {
        const expanded = meta.classList.toggle('is-expanded');
        metaToggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        metaToggle.textContent = expanded ? 'Show Less' : 'Show More';
      });
    }

    window.requestAnimationFrame(() => {
      const activeItem = this._mediaItems && this._mediaItems[this._mediaIndex];
      if (activeItem) this._resumeMediaItem(activeItem);
    });

    // start modal autoplay if enabled on the modal element
    try {
      const autoplayEnabled = this.modal.dataset.modalAutoplay === 'true';
      const autoplayInterval = Number(this.modal.dataset.modalAutoplayInterval) || 5;
      if (autoplayEnabled) this._startAutoplay(autoplayInterval);
    } catch (e) {
      // ignore if dataset not present
    }
  }

  close() {
    this._submittedFromModal = false;

    if (this._mediaSwipeCleanup) {
      this._mediaSwipeCleanup();
      this._mediaSwipeCleanup = null;
    }

    // stop all media
    this.contentEl.querySelectorAll('video').forEach((video) => {
      video.pause();
    });
    this.contentEl.querySelectorAll('iframe').forEach((iframe) => {
      iframe.src = '';
    });

    // stop autoplay timers if any
    this._stopAutoplay();

    this.modal.hidden = true;
    document.body.style.overflow = '';
    document.removeEventListener('keydown', this._handleKeyDown);
    this.contentEl.innerHTML = '';
  }

  _onKeyDown(e) {
    if (e.key === 'Escape') this.close();
  }

  _startAutoplay(intervalSeconds) {
    this._stopAutoplay();
    if (!this._mediaItems || this._mediaItems.length <= 1) return;
    this._autoplayInterval = (Number(intervalSeconds) || 5) * 1000;
    this._autoplayTimer = setInterval(() => {
      this._setActiveMedia(this._mediaIndex + 1);
    }, this._autoplayInterval);
  }

  _stopAutoplay() {
    if (this._autoplayTimer) {
      clearInterval(this._autoplayTimer);
      this._autoplayTimer = null;
    }
    if (this._autoplayResumeTimer) {
      clearTimeout(this._autoplayResumeTimer);
      this._autoplayResumeTimer = null;
    }
  }

  _userInteracted() {
    // when user interacts, pause autoplay and resume after one interval
    this._stopAutoplay();
    try {
      const interval = Number(this.modal.dataset.modalAutoplayInterval) || 5;
      this._autoplayResumeTimer = setTimeout(() => {
        if (!this.modal.hidden && this.modal.dataset.modalAutoplay === 'true') {
          this._startAutoplay(interval);
        }
      }, (Number(interval) || 5) * 1000);
    } catch (e) {
      // noop
    }
  }

  _onProductAdded() {
    if (!this._submittedFromModal || this.modal.hidden) return;

    this._submittedFromModal = false;
    window.requestAnimationFrame(() => {
      this.close();
    });
  }

  _goToRelative(offset) {
    if (!this._blockIds || !this._blockIds.length) return;
    const idx = this._blockIds.indexOf(this._currentBlockId);
    if (idx === -1) return;
    const next = (idx + offset + this._blockIds.length) % this._blockIds.length;
    const nextId = this._blockIds[next];
    if (nextId) this.open(nextId);
  }

  _updateNavState() {
    if (!this._navPrev || !this._navNext) return;
    if (!this._blockIds || this._blockIds.length <= 1) {
      this._navPrev.style.display = 'none';
      this._navNext.style.display = 'none';
      return;
    }
    this._navPrev.style.display = '';
    this._navNext.style.display = '';
  }

  _initMediaSlider() {
    const slider = this.contentEl.querySelector('[data-media-slider]');
    if (!slider) return;

    if (this._mediaSwipeCleanup) {
      this._mediaSwipeCleanup();
      this._mediaSwipeCleanup = null;
    }

    const items = [...slider.querySelectorAll('[data-media-item]')];
    if (!items.length) return;

    const dots = [...slider.querySelectorAll('[data-media-to]')];
    const prevBtn = slider.querySelector('[data-media-prev]');
    const nextBtn = slider.querySelector('[data-media-next]');

    this._mediaItems = items;
    this._mediaIndex = Math.max(
      0,
      items.findIndex((item) => item.classList.contains('is-active'))
    );

    // If on mobile, convert absolute stacked items into a horizontal list
    // so we can animate translate3d for a sliding effect similar to product gallery.
    const isMobileView = window.matchMedia('(max-width: 767.98px)').matches;
    if (isMobileView && this._mediaItems.length > 1) {
      let list = slider.querySelector('.lwp-modal__media-list');
      if (!list) {
        list = document.createElement('div');
        list.className = 'lwp-modal__media-list';
        this._mediaItems.forEach((it) => list.appendChild(it));
        slider.appendChild(list);
      }
      this._mediaList = list;
      this._mediaList.style.display = 'flex';
      this._mediaList.style.width = '100%';
      this._mediaList.style.transition = 'transform 300ms cubic-bezier(0.4, 0, 0.22, 1)';
      this._mediaList.style.willChange = 'transform';
      this._mediaItems.forEach((it) => {
        it.style.position = 'relative';
        it.style.display = 'block';
        it.style.flex = '0 0 100%';
        it.style.maxWidth = '100%';
      });
    } else {
      this._mediaList = null;
    }

    // first active media should be mounted/played
    this._setActiveMedia(this._mediaIndex, { immediate: true });

    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        this._userInteracted();
        this._setActiveMedia(this._mediaIndex - 1);
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        this._userInteracted();
        this._setActiveMedia(this._mediaIndex + 1);
      });
    }

    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        this._userInteracted();
        const target = Number(dot.dataset.mediaTo);
        if (Number.isNaN(target)) return;
        this._setActiveMedia(target);
      });
    });

    // pause autoplay when user interacts via pointer (touch/mouse)
    if (!this._mediaPointerHandler) {
      this._mediaPointerHandler = () => this._userInteracted();
      slider.addEventListener('pointerdown', this._mediaPointerHandler);
    }

    this._initMobileMediaSwipe(slider);
  }

  _initMobileMediaSwipe(slider) {
    if (!window.matchMedia('(max-width: 767.98px)').matches) return;
    if (!this._mediaItems || this._mediaItems.length <= 1) return;

    let startX = 0;
    let startY = 0;
    let deltaX = 0;
    let deltaY = 0;
    let tracking = false;

    const onTouchStart = (event) => {
      if (!event.touches || event.touches.length !== 1) return;
      const touch = event.touches[0];
      startX = touch.clientX;
      startY = touch.clientY;
      deltaX = 0;
      deltaY = 0;
      tracking = true;
    };

    const onTouchMove = (event) => {
      if (!tracking || !event.touches || event.touches.length !== 1) return;
      const touch = event.touches[0];
      deltaX = touch.clientX - startX;
      deltaY = touch.clientY - startY;
    };

    const onTouchEnd = () => {
      if (!tracking) return;
      tracking = false;

      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);
      const meetsDistance = absX >= 45;
      const isHorizontalSwipe = absX > absY * 1.2;

      if (!meetsDistance || !isHorizontalSwipe) return;

      this._userInteracted();
      if (deltaX < 0) {
        this._setActiveMedia(this._mediaIndex + 1);
      } else {
        this._setActiveMedia(this._mediaIndex - 1);
      }
    };

    slider.addEventListener('touchstart', onTouchStart, { passive: true });
    slider.addEventListener('touchmove', onTouchMove, { passive: true });
    slider.addEventListener('touchend', onTouchEnd, { passive: true });
    slider.addEventListener('touchcancel', onTouchEnd, { passive: true });

    this._mediaSwipeCleanup = () => {
      slider.removeEventListener('touchstart', onTouchStart);
      slider.removeEventListener('touchmove', onTouchMove);
      slider.removeEventListener('touchend', onTouchEnd);
      slider.removeEventListener('touchcancel', onTouchEnd);
    };
  }

  _setActiveMedia(nextIndex, { immediate = false } = {}) {
    if (!this._mediaItems || !this._mediaItems.length) return;

    const total = this._mediaItems.length;
    let normalized = nextIndex;

    if (normalized < 0) normalized = total - 1;
    if (normalized >= total) normalized = 0;

    const currentItem = this._mediaItems[this._mediaIndex];
    const nextItem = this._mediaItems[normalized];
    if (!nextItem) return;

    if (!immediate && currentItem && currentItem !== nextItem) {
      this._pauseMediaItem(currentItem);
    }

    this._mediaItems.forEach((item, i) => {
      const active = i === normalized;
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-hidden', active ? 'false' : 'true');
    });

    const dots = this.contentEl.querySelectorAll('[data-media-to]');
    dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === normalized);
      dot.setAttribute('aria-current', i === normalized ? 'true' : 'false');
    });

    this._mediaIndex = normalized;
    // if we built a mobile list, slide it into place using translate3d for smoother animation
    if (this._mediaList) {
      const itemWidth = this._mediaItems[0].getBoundingClientRect().width || this.contentEl.querySelector('.lwp-modal__left').getBoundingClientRect().width;
      const offset = normalized * itemWidth * -1;
      this._mediaList.style.transform = `translate3d(${offset}px,0,0)`;
    }

    this._resumeMediaItem(nextItem);
  }

  _pauseMediaItem(item) {
    const video = item.querySelector('video');
    if (video) video.pause();

    const iframe = item.querySelector('iframe');
    if (iframe && iframe.src) {
      iframe.dataset.src = iframe.src;
      iframe.src = '';
    }
  }

  _resumeMediaItem(item) {
    if (item.dataset.mediaType === 'video') {
      const wrap = item.querySelector('.lwp-modal__video-wrap');
      if (!wrap) return;

      if (!wrap.dataset.videoInited) {
        this._initVideo(wrap);
        wrap.dataset.videoInited = 'true';
      }

      const iframe = item.querySelector('iframe');
      if (iframe && !iframe.src && iframe.dataset.src) {
        iframe.src = iframe.dataset.src;
      }

      const video = item.querySelector('video');
      if (video) {
        video.play().catch(() => {});
      }
    }
  }

  _initVideo(wrap) {
    const url = wrap.dataset.videoUrl;
    if (!url) return;

    let el;

    if (/youtube\.com|youtu\.be/.test(url)) {
      const id = this._getYouTubeId(url);
      if (!id) return;
      el = document.createElement('iframe');
      el.src = `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&rel=0`;
      el.allow = 'autoplay; encrypted-media; fullscreen';
      el.allowFullscreen = true;
      el.className = 'lwp-modal__iframe';
    } else if (/tiktok\.com/.test(url)) {
      const id = this._getTikTokId(url);
      if (!id) return;
      el = document.createElement('iframe');
      el.src = `https://www.tiktok.com/embed/v2/${id}`;
      el.allow = 'autoplay';
      el.className = 'lwp-modal__iframe';
    } else {
      // direct video (mp4, webm, etc.)
      el = document.createElement('video');
      el.src = url;
      el.autoplay = true;
      el.muted = true;
      el.loop = true;
      el.playsInline = true;
      el.className = 'lwp-modal__video';

      // mute toggle button
      const muteBtn = document.createElement('button');
      muteBtn.type = 'button';
      muteBtn.className = 'lwp-modal__mute-btn is-muted';
      muteBtn.setAttribute('aria-label', 'Toggle mute');
      muteBtn.innerHTML = this._mutedIcon();
      muteBtn.addEventListener('click', () => {
        el.muted = !el.muted;
        muteBtn.classList.toggle('is-muted', el.muted);
        muteBtn.innerHTML = el.muted ? this._mutedIcon() : this._unmutedIcon();
      });
      wrap.appendChild(muteBtn);
    }

    if (el) wrap.prepend(el);
  }

  _mutedIcon() {
    return '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/></svg>';
  }

  _unmutedIcon() {
    return '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M19.07 4.93a10 10 0 010 14.14M15.54 8.46a5 5 0 010 7.07"/></svg>';
  }

  _getYouTubeId(url) {
    const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([^&\n?#]+)/);
    return m ? m[1] : null;
  }

  _getTikTokId(url) {
    const m = url.match(/tiktok\.com\/@[^/]+\/video\/(\d+)/);
    return m ? m[1] : null;
  }

}

// Init modal for every live-with-povison section on the page
document.querySelectorAll('.section--live-with-povison').forEach((section) => {
  new LWPModal(section);
});